import { useMemo, useState, type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SuiteRunEditDialog } from '@/features/executions/components/SuiteRunEditDialog';
import { TestRunEditDialog } from '@/features/executions/components/TestRunEditDialog';
import { useSuiteRunEditDialog } from '@/features/executions/components/useSuiteRunEditDialog';
import { useSuiteRunsTableRowActions } from '@/features/executions/components/useSuiteRunsTableRowActions';
import { useTestRunEditDialog } from '@/features/executions/components/useTestRunEditDialog';
import { useTestRunsTableRowActions } from '@/features/executions/components/useTestRunsTableRowActions';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { buildSuiteDetailPath } from '@/features/shared/components/tableSystem/tableRoutes';
import {
  TableV2MetricCells,
  TableV2MetricHeaders,
} from '@/features/shared/components/tableSystem/TableV2MetricColumns';
import { tableV2NestedCarrierRowSx } from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2ChevronButton,
  TableV2DateTimeCell,
  TableV2HeaderLabel,
  TableV2NameCell,
  TableV2ProjectCell,
  TableV2StatusChip,
  TableV2SortableHeader,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2ChevronCellSx,
  tableV2ChevronColumnWidth,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { buildTableV2BodyRowSurfaceSx } from '@/features/shared/components/tableSystem/TableV2RowSurface';
import { buildTagColorByName, mergeTagColorByName } from '@/features/shared/tags';
import { DeleteSuiteDialog, EditSuiteDialog } from '@/features/suites/components/dialogs';
import {
  defaultSuiteV2Sort,
  resolveSuiteRunChildCount,
} from '@/features/suites/components/SuitesTableV2.helpers';
import { useSuitesTableEditDialog } from '@/features/suites/components/useSuitesTableEditDialog';
import { useSuitesTableRowActions } from '@/features/suites/components/useSuitesTableRowActions';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { resolveVisibleMetricThresholdKeys } from '@/shared/utils/metricsThresholds';
import { s } from '@/theme/tokens';
import { TableCell, TableRow } from '@/ui/primitives';
import { buildSuiteColgroup, buildSuiteSkeletonColumns } from './SuitesTableV2Layout';
import { SuiteExpandedRuns } from './SuitesTableV2NestedRuns';
import type { SortableSuiteField, SuitesTableV2Props } from './SuitesTableV2.types';
import type { TagColor } from '@/shared/data/tags';
import type { MetricThresholdGroupKey } from '@/shared/utils/metricsThresholds';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  MetricThresholdGroup,
  ProjectDto,
  QueryResult,
  SuiteListItemDto,
  SuiteRunDto,
  TestRunDto,
} from '@flowqa/shared';

const SuiteRow = ({
  actionsDisabled,
  deleteRowPendingId,
  isLastVisibleRow,
  linksDisabled,
  row,
  rowProject,
  showActions,
  showProjectName,
  showProjectColumn,
  sortingDisabled,
  nestedSuiteRunsLatestOnly,
  suiteRunsScopeKey,
  suiteRunsQueryFn,
  suiteRunDeleteRowPendingId,
  suiteRunUpdateRowTagsColorPendingId,
  tagColorByName,
  testRunsQueryFn,
  testRunDeleteRowPendingId,
  testRunUpdateRowTagsColorPendingId,
  thresholds,
  visibleMetricKeys,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onDeleteSuiteRunRow,
  onDeleteTestRunRow,
  onEditRow,
  onEditSuiteRunRow,
  onEditTestRunRow,
  onUpdateRowTagsColor,
  onUpdateSuiteRunRowTagsColor,
  onUpdateTestRunRowTagsColor,
}: {
  actionsDisabled: boolean;
  deleteRowPendingId: string | null;
  isLastVisibleRow: boolean;
  linksDisabled: boolean;
  row: SuiteListItemDto;
  rowProject: Pick<ProjectDto, 'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'name'> | null;
  showActions: boolean;
  showProjectName: boolean;
  showProjectColumn: boolean;
  sortingDisabled: boolean;
  nestedSuiteRunsLatestOnly: boolean;
  suiteRunsScopeKey?: string;
  suiteRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<SuiteRunDto>>;
  suiteRunDeleteRowPendingId: string | null;
  suiteRunUpdateRowTagsColorPendingId: string | null;
  tagColorByName: Partial<Record<string, TagColor>>;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  testRunDeleteRowPendingId: string | null;
  testRunUpdateRowTagsColorPendingId: string | null;
  thresholds: MetricThresholdGroup;
  visibleMetricKeys: MetricThresholdGroupKey[];
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: SuiteListItemDto) => void;
  onDeleteSuiteRunRow: (row: SuiteRunDto) => void;
  onDeleteTestRunRow: (row: TestRunDto) => void;
  onEditRow: (row: SuiteListItemDto) => void;
  onEditSuiteRunRow: (row: SuiteRunDto) => void;
  onEditTestRunRow: (row: TestRunDto) => void;
  onUpdateRowTagsColor: (input: {
    row: SuiteListItemDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  onUpdateSuiteRunRowTagsColor: (input: {
    row: SuiteRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  onUpdateTestRunRowTagsColor: (input: {
    row: TestRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const latestRun = row.latestRun ?? null;
  const metrics = row.metrics ?? null;
  const suiteName = row.displayName ?? row.name;
  const suiteRunCount = resolveSuiteRunChildCount(row);

  return (
    <>
      <TableRow
        sx={buildTableV2BodyRowSurfaceSx({
          hasRenderedDivider: !expanded && !isLastVisibleRow,
        })}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse suite runs' : 'Expand suite runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell
            href={linksDisabled ? null : buildSuiteDetailPath(row.slug)}
            primary={suiteName}
            secondary={showProjectName && !showProjectColumn ? (row.project?.name ?? null) : null}
          />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={latestRun?.status ?? null} />
        </TableCell>
        <TableCell>
          <TableV2DateTimeCell value={latestRun?.timestamp ?? null} />
        </TableCell>
        <TableV2MetricCells
          metrics={metrics}
          thresholds={thresholds}
          visibleMetricKeys={visibleMetricKeys}
        />
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
        </TableCell>
        {showProjectColumn ? (
          <TableCell>
            <TableV2ProjectCell project={rowProject} fallbackName={row.project?.name} />
          </TableCell>
        ) : null}
        {showActions ? (
          <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
            {actionsDisabled ? null : (
              <TableActionsCell
                rowName={suiteName}
                verticalAlign="center"
                deleteActionLabel={getEntityActionLabel('delete', 'Suite')}
                editActionLabel={getEntityActionLabel('edit', 'Suite')}
                editTagsEntityNoun="suite"
                rowTags={row.tags ?? []}
                onEdit={() => {
                  onEditRow(row);
                }}
                onUpdateTagsColor={(input) => {
                  onUpdateRowTagsColor({ row, ...input });
                }}
                updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
                onDelete={() => {
                  onDeleteRow(row);
                }}
                disableDelete={deleteRowPendingId === row.id}
              />
            )}
          </TableCell>
        ) : null}
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? {
                '& > td': {
                  borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                },
              }
            : {},
        ]}
      >
        <TableCell
          colSpan={
            buildSuiteSkeletonColumns(visibleMetricKeys, showProjectColumn, showActions).length
          }
        >
          <SuiteExpandedRuns
            actionsDisabled={actionsDisabled}
            childCount={suiteRunCount}
            deleteRowPendingId={suiteRunDeleteRowPendingId}
            expanded={expanded}
            linksDisabled={linksDisabled}
            organisationId={row.organisationId}
            projectId={row.projectId}
            showActions={showActions}
            suiteId={row.id}
            latestOnly={nestedSuiteRunsLatestOnly}
            latestRunId={latestRun?.id ?? null}
            suiteRunsScopeKey={suiteRunsScopeKey}
            suiteRunsQueryFn={suiteRunsQueryFn}
            sortingDisabled={sortingDisabled}
            tagColorByName={tagColorByName}
            testRunsQueryFn={testRunsQueryFn}
            testRunDeleteRowPendingId={testRunDeleteRowPendingId}
            testRunUpdateRowTagsColorPendingId={testRunUpdateRowTagsColorPendingId}
            updateRowTagsColorPendingId={suiteRunUpdateRowTagsColorPendingId}
            onDeleteSuiteRunRow={onDeleteSuiteRunRow}
            onDeleteTestRunRow={onDeleteTestRunRow}
            onEditSuiteRunRow={onEditSuiteRunRow}
            onEditTestRunRow={onEditTestRunRow}
            onUpdateSuiteRunRowTagsColor={onUpdateSuiteRunRowTagsColor}
            onUpdateTestRunRowTagsColor={onUpdateTestRunRowTagsColor}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export const SuitesTableV2 = ({
  actionsDisabled = false,
  active,
  fixedFilters,
  forceLoading = false,
  hideFooter = false,
  initialPageSize,
  linksDisabled = false,
  noRowsText = 'No data',
  onQueryMeta,
  pageSizeOptions,
  projectOptions = [],
  queryFn,
  queryKey,
  requireAuth = true,
  search,
  selectedProjectId = null,
  showActions = true,
  showProjectName = true,
  sortingDisabled = false,
  nestedSuiteRunsLatestOnly = false,
  tagColorByName: tagColorByNameOverride,
  suiteRunsScopeKey,
  suiteRunsQueryFn,
  tagOptionsOverride = [],
  testRunsQueryFn,
}: SuitesTableV2Props): ReactElement => {
  const [sort, setSort] = useState<ApiSort>(defaultSuiteV2Sort);
  const showProjectColumn = selectedProjectId === null && showProjectName;
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const visibleMetricKeys = resolveVisibleMetricThresholdKeys(thresholds.suites);
  const skeletonColumns = buildSuiteSkeletonColumns(
    visibleMetricKeys,
    showProjectColumn,
    showActions,
  );
  const tagColorByName = useMemo(
    () => mergeTagColorByName(buildTagColorByName(tagOptionsOverride), tagColorByNameOverride),
    [tagColorByNameOverride, tagOptionsOverride],
  );
  const {
    editEditor,
    editErrors,
    editForm,
    editTarget,
    handleEditClose,
    handleEditRow,
    handleEditSave,
  } = useSuitesTableEditDialog();
  const {
    editLoading: suiteRunEditLoading,
    editTarget: suiteRunEditTarget,
    onEditClose: onSuiteRunEditClose,
    onEditRow: onEditSuiteRunRow,
    onEditSave: onSuiteRunEditSave,
  } = useSuiteRunEditDialog(selectedProjectId);
  const {
    editLoading: testRunEditLoading,
    editTarget: testRunEditTarget,
    onEditClose: onTestRunEditClose,
    onEditRow: onEditTestRunRow,
    onEditSave: onTestRunEditSave,
  } = useTestRunEditDialog(selectedProjectId);
  const projectById = useMemo(
    () => new Map(projectOptions.map((project) => [project.id, project])),
    [projectOptions],
  );
  const {
    deleteLoading,
    deleteRowPendingId,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useSuitesTableRowActions({ selectedProjectId });
  const {
    deleteLoading: suiteRunDeleteLoading,
    deleteRowPendingId: suiteRunDeleteRowPendingId,
    deleteTarget: suiteRunDeleteTarget,
    onDeleteClose: onSuiteRunDeleteClose,
    onDeleteConfirm: onSuiteRunDeleteConfirm,
    onDeleteRow: onDeleteSuiteRunRow,
    onUpdateRowTagsColor: onUpdateSuiteRunRowTagsColor,
    updateRowTagsColorPendingId: suiteRunUpdateRowTagsColorPendingId,
  } = useSuiteRunsTableRowActions({ selectedProjectId });
  const {
    deleteLoading: testRunDeleteLoading,
    deleteRowPendingId: testRunDeleteRowPendingId,
    deleteTarget: testRunDeleteTarget,
    onDeleteClose: onTestRunDeleteClose,
    onDeleteConfirm: onTestRunDeleteConfirm,
    onDeleteRow: onDeleteTestRunRow,
    onUpdateRowTagsColor: onUpdateTestRunRowTagsColor,
    updateRowTagsColorPendingId: testRunUpdateRowTagsColorPendingId,
  } = useTestRunsTableRowActions({ selectedProjectId });

  const handleSortChange = (field: SortableSuiteField): void => {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const header = (
    <TableRow>
      <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          disabled={sortingDisabled}
          field="name"
          label="Suite"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2HeaderLabel label="Latest Status" />
      </TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          disabled={sortingDisabled}
          field="latestRunTimestamp"
          label="Ran At"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableV2MetricHeaders
        sort={sort}
        sortingDisabled={sortingDisabled}
        visibleMetricKeys={visibleMetricKeys}
        onSortChange={handleSortChange}
      />
      <TableCell>
        <TableV2HeaderLabel label="Tags" />
      </TableCell>
      {showProjectColumn ? (
        <TableCell>
          <TableV2HeaderLabel label="Project" />
        </TableCell>
      ) : null}
      {showActions ? (
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          <TableV2ActionsHeaderIcon />
        </TableCell>
      ) : null}
    </TableRow>
  );

  return (
    <>
      <TableV2QueryShell
        active={active}
        ariaLabel="Suites"
        colgroup={buildSuiteColgroup(visibleMetricKeys, showProjectColumn, showActions)}
        emptyText={noRowsText}
        entityLabel="suites"
        fixedFilters={fixedFilters}
        forceLoading={forceLoading}
        header={header}
        hideFooter={hideFooter}
        initialPageSize={initialPageSize}
        pageSizeOptions={pageSizeOptions}
        queryFn={queryFn}
        queryKey={queryKey}
        requireAuth={requireAuth}
        search={search}
        skeletonColumns={skeletonColumns}
        sorts={[sort]}
        onQueryMeta={onQueryMeta}
        renderRow={(row, context) => (
          <SuiteRow
            key={row.id}
            actionsDisabled={actionsDisabled}
            deleteRowPendingId={deleteRowPendingId}
            isLastVisibleRow={context.isLastVisibleRow}
            linksDisabled={linksDisabled}
            row={row}
            rowProject={projectById.get(row.projectId) ?? null}
            showActions={showActions}
            showProjectName={showProjectName}
            showProjectColumn={showProjectColumn}
            sortingDisabled={sortingDisabled}
            nestedSuiteRunsLatestOnly={nestedSuiteRunsLatestOnly}
            suiteRunDeleteRowPendingId={suiteRunDeleteRowPendingId}
            suiteRunUpdateRowTagsColorPendingId={suiteRunUpdateRowTagsColorPendingId}
            suiteRunsScopeKey={suiteRunsScopeKey}
            suiteRunsQueryFn={suiteRunsQueryFn}
            tagColorByName={tagColorByName}
            testRunDeleteRowPendingId={testRunDeleteRowPendingId}
            testRunUpdateRowTagsColorPendingId={testRunUpdateRowTagsColorPendingId}
            testRunsQueryFn={testRunsQueryFn}
            thresholds={thresholds.suites}
            visibleMetricKeys={visibleMetricKeys}
            updateRowTagsColorPendingId={updateRowTagsColorPendingId}
            onDeleteRow={onDeleteRow}
            onDeleteSuiteRunRow={onDeleteSuiteRunRow}
            onDeleteTestRunRow={onDeleteTestRunRow}
            onEditRow={handleEditRow}
            onEditSuiteRunRow={onEditSuiteRunRow}
            onEditTestRunRow={onEditTestRunRow}
            onUpdateRowTagsColor={onUpdateRowTagsColor}
            onUpdateSuiteRunRowTagsColor={onUpdateSuiteRunRowTagsColor}
            onUpdateTestRunRowTagsColor={onUpdateTestRunRowTagsColor}
          />
        )}
      />
      <EditSuiteDialog
        open={editTarget !== null}
        saving={editEditor.isPending}
        values={editForm.values}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        errors={{ name: editErrors.fieldErrorProps('name') }}
        errorMessage={editForm.submitError}
        onClose={handleEditClose}
        onSave={handleEditSave}
        handlers={{
          onNameChange: (value) => {
            editForm.setField('name', value);
          },
        }}
      />
      <SuiteRunEditDialog
        open={suiteRunEditTarget !== null}
        execution={suiteRunEditTarget}
        loading={suiteRunEditLoading}
        onClose={onSuiteRunEditClose}
        onSave={onSuiteRunEditSave}
      />
      <TestRunEditDialog
        open={testRunEditTarget !== null}
        execution={testRunEditTarget}
        loading={testRunEditLoading}
        onClose={onTestRunEditClose}
        onSave={onTestRunEditSave}
      />
      <DeleteSuiteDialog
        open={deleteTarget !== null}
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <ConfirmDialog
        open={suiteRunDeleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'suiteRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'suiteRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'suiteRun',
        })}
        confirmColor="error"
        loading={suiteRunDeleteLoading}
        onClose={onSuiteRunDeleteClose}
        onConfirm={onSuiteRunDeleteConfirm}
      />
      <ConfirmDialog
        open={testRunDeleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'testRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'testRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'testRun',
        })}
        confirmColor="error"
        loading={testRunDeleteLoading}
        onClose={onTestRunDeleteClose}
        onConfirm={onTestRunDeleteConfirm}
      />
    </>
  );
};
